import type { Action } from "svelte/action"
import {
  Directions,
  Orientations,
  type Direction,
  type Orientation,
} from "../constants"
import type { ElementCollectionStore } from "./collectionContext"
import type { EventHandler } from "./event"
import { nextKey, prevKey } from "./keyboard"

export type ElementCollectionListenersMap = Map<
  string | number,
  EventHandler<KeyboardEvent>
>

function setupKeyboardNavigation(
  elements: HTMLElement[],
  listenersMap: ElementCollectionListenersMap,
  orientation: Orientation,
  dir: Direction,
  loop: boolean
): void {
  const enabledElements = elements.filter((el) => !el.dataset.disabled)

  elements.forEach((el) => {
    if (!el.id) {
      throw new TypeError(
        "enableGroupKeyboardNavigation requires all elements to have a unique id, consider using UICL generateId()"
      )
    }
    const previousListener = listenersMap.get(el.id)
    if (previousListener) {
      el.removeEventListener("keydown", previousListener)
    }

    const enabledIndex = enabledElements.indexOf(el)
    const listener = (e: KeyboardEvent) => {
      if (e.key === nextKey(orientation, dir)) {
        e.preventDefault()
        nextItem(enabledElements, enabledIndex, loop)?.focus()
      } else if (e.key === prevKey(orientation, dir)) {
        e.preventDefault()
        prevItem(enabledElements, enabledIndex, loop)?.focus()
      }
    }

    listenersMap.set(el.id, listener)
    el.addEventListener("keydown", listener)
  })
}

/**
 * Automatically add and remove appropriate keyboard event listeners
 * to elements within a collection that are navigated together
 * e.g. Tabs, ButtonGroup, Select
 */
export function enableGroupKeyboardNavigation(
  collection: ElementCollectionStore | HTMLElement[],
  listenersMap: ElementCollectionListenersMap,
  orientation: Orientation = Orientations.HORIZONTAL,
  dir: Direction = Directions.LTR,
  loop = true
): void {
  if (Array.isArray(collection)) {
    setupKeyboardNavigation(collection, listenersMap, orientation, dir, loop)
  } else {
    collection.subscribe((elements) => {
      setupKeyboardNavigation(elements, listenersMap, orientation, dir, loop)
    })
  }
}

export function nextItem<T>(
  items: T[],
  index: number,
  loop = true
): T | undefined {
  return index === items.length - 1
    ? loop
      ? items[0]
      : items[index]
    : items[index + 1]
}
export function prevItem<T>(
  items: T[],
  index: number,
  loop = true
): T | undefined {
  return index === 0
    ? loop
      ? items[items.length - 1]
      : items[index]
    : items[index - 1]
}

interface GroupKeyboardNavigationOptions {
  orientation?: Orientation
  dir?: Direction
  loop?: boolean
}

/**
 * Svelte action that enables keyboard navigation for a group of child elements.
 * Automatically handles arrow key navigation with support for different orientations,
 * text directions, and looping behavior.
 *
 * @param node - The parent HTML element containing navigable direct children
 * @param options - Configuration options for navigation behavior
 * @param options.orientation - Layout orientation (HORIZONTAL or VERTICAL), defaults to HORIZONTAL
 * @param options.dir - Text direction (LTR or RTL), defaults to LTR
 * @param options.loop - Whether navigation wraps around at boundaries, defaults to true
 *
 * @example
 * ```svelte
 * <div use:groupKeyboardNavigation={{ orientation: 'vertical', loop: false }}>
 *   <button>Item 1</button>
 *   <button>Item 2</button>
 *   <button>Item 3</button>
 * </div>
 * ```
 *
 * @returns Svelte action object
 */
export const groupKeyboardNavigation = ((
  node,
  options: GroupKeyboardNavigationOptions = {}
) => {
  let {
    orientation = Orientations.HORIZONTAL,
    dir = Directions.LTR,
    loop = true,
  } = options

  const listenersMap: ElementCollectionListenersMap = new Map()

  function updateNavigation() {
    const children = Array.from(node.children) as HTMLElement[]
    enableGroupKeyboardNavigation(
      children,
      listenersMap,
      orientation,
      dir,
      loop
    )
  }

  const observer = new MutationObserver(updateNavigation)
  observer.observe(node, { childList: true })

  updateNavigation()

  return {
    update(newOptions: GroupKeyboardNavigationOptions = {}) {
      orientation = newOptions.orientation ?? Orientations.HORIZONTAL
      dir = newOptions.dir ?? Directions.LTR
      loop = newOptions.loop ?? true
      listenersMap.clear()
      updateNavigation()
    },
    destroy() {
      observer.disconnect()
      listenersMap.forEach((listener, id) => {
        const element = node.querySelector(`#${id}`) as HTMLElement
        if (element) {
          element.removeEventListener("keydown", listener)
        }
      })
      listenersMap.clear()
    },
  }
}) satisfies Action<HTMLElement, GroupKeyboardNavigationOptions | undefined>
